import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertList } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { FormActions } from "@jbnl/shared-ui";
import { datumKort } from "./datum";
import {
  useSetPromotieplaatsen,
  type Promotieplaats,
  type Wedstrijddag,
} from "./useWedstrijden";

/** The rows of every baan, keyed on the baan - the same shape `Speelschema` edits in. */
type PerBaan = Record<string, Promotieplaats[]>;

/**
 * Per baan, not per dag (ADR-0062), and positions rather than a count: Breukelen sent nrs 1 and
 * 3 to one halve finale and nr 2 to another, which a count cannot say. The preview is the
 * server's own sentence, so a second phrasing here cannot drift from the boekje.
 */
export function Promotieplaatsen({
  seizoenId,
  dag,
  dagen,
  niveaus = [],
}: {
  seizoenId: string;
  dag: Wedstrijddag;
  dagen: Wedstrijddag[];
  niveaus?: { id: string; sortOrder: number | string }[];
}) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  const opslaan = useSetPromotieplaatsen(seizoenId);

  const opgeslagen = useMemo<PerBaan>(
    () =>
      Object.fromEntries(
        dag.wedstrijdbanen.map((baan) => [baan.id, [...baan.promotieplaatsen]]),
      ),
    [dag.wedstrijdbanen],
  );

  // `null` is "nothing typed yet", reading through to whatever the server holds - the same
  // reasoning Speelschema records: it lets a refetch land without an effect to re-seed from.
  const [bewerkt, setBewerkt] = useState<PerBaan | null>(null);
  const rijen = bewerkt ?? opgeslagen;

  // A later dag (the server's rule) at a higher niveau, so a halve finale never offers a
  // kwalificatie. Higher, not next: Brandbestrijding skips straight to the landelijke finale.
  // Date only until the niveaus have loaded.
  const bestemmingen = useMemo(() => {
    const volgorde = new Map(niveaus.map((n) => [n.id, Number(n.sortOrder)]));
    const eigen = volgorde.get(dag.niveauId);
    return dagen
      .filter(
        (d) =>
          d.date > dag.date &&
          (eigen === undefined ||
            (volgorde.get(d.niveauId) ?? Infinity) > eigen),
      )
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [dagen, dag.date, dag.niveauId, niveaus]);

  function zet(baanId: string, plaatsen: Promotieplaats[]) {
    setBewerkt({ ...rijen, [baanId]: plaatsen });
  }

  const gewijzigd = dag.wedstrijdbanen.filter(
    (baan) =>
      JSON.stringify(rijen[baan.id]) !== JSON.stringify(opgeslagen[baan.id]),
  );

  // A row whose bestemming is still empty cannot be saved and must not be dropped silently -
  // it is a plaats the beheerder has begun to state and has not finished.
  const onvolledig = dag.wedstrijdbanen.some((baan) =>
    (rijen[baan.id] ?? []).some((plaats) => !plaats.naarWedstrijddagId),
  );

  // One request per changed baan, because the announcement belongs to a baan. In sequence rather
  // than at once: each answers with the whole dag, and the last answer in is the one the cache
  // keeps. Local edits are dropped only once every request has landed, so a failure halfway
  // leaves what the beheerder typed on screen.
  async function slaGewijzigdeBanenOp() {
    for (const baan of gewijzigd) {
      await opslaan.mutateAsync({
        baanId: baan.id,
        plaatsen: rijen[baan.id] ?? [],
      });
    }
    setBewerkt(null);
  }

  if (dag.wedstrijdbanen.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("promoties.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">{t("promoties.uitleg")}</p>

        {bestemmingen.length === 0 ? (
          <Alert>{t("promoties.geenBestemming")}</Alert>
        ) : (
          dag.wedstrijdbanen.map((baan) => (
            <Baan
              key={baan.id}
              baan={baan}
              plaatsen={rijen[baan.id] ?? []}
              bestemmingen={bestemmingen}
              vuil={gewijzigd.some((b) => b.id === baan.id)}
              onChange={(plaatsen) => zet(baan.id, plaatsen)}
            />
          ))
        )}

        <FormActions>
          {opslaan.error && (
            <p className="text-sm text-destructive">{opslaan.error.message}</p>
          )}
          {onvolledig && (
            <p className="text-sm text-muted-foreground">
              {t("promoties.kiesBestemming")}
            </p>
          )}
          <Button
            type="button"
            disabled={opslaan.isPending || gewijzigd.length === 0 || onvolledig}
            onClick={() => void slaGewijzigdeBanenOp()}
          >
            {opslaan.isPending
              ? t("common:actions.busy")
              : t("promoties.opslaan")}
          </Button>
        </FormActions>
      </CardContent>
    </Card>
  );
}

function Baan({
  baan,
  plaatsen,
  bestemmingen,
  vuil,
  onChange,
}: {
  baan: Wedstrijddag["wedstrijdbanen"][number];
  plaatsen: Promotieplaats[];
  bestemmingen: Wedstrijddag[];
  vuil: boolean;
  onChange: (plaatsen: Promotieplaats[]) => void;
}) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  // Number() because openapi-typescript widens every int32 in this schema to `number | string`,
  // and a positie is the one field on this card that is both sorted and counted on. Normalised
  // once here so nothing below has to remember it.
  const gesorteerd = plaatsen
    .map((p) => ({
      positie: Number(p.positie),
      naarWedstrijddagId: p.naarWedstrijddagId,
    }))
    .sort((a, b) => a.positie - b.positie);

  // A destination that runs no baan of this klasse. A warning and never a block: a beheerder
  // states this months before a finale has its banen (ADR-0062).
  const zonderKlasse = gesorteerd
    .map((plaats) =>
      bestemmingen.find((d) => d.id === plaats.naarWedstrijddagId),
    )
    .filter(
      (d): d is Wedstrijddag =>
        d != null &&
        !d.wedstrijdbanen.some((b) => b.klasseId === baan.klasseId),
    );

  return (
    <section className="space-y-3" aria-labelledby={`promoties-${baan.id}`}>
      <h3 id={`promoties-${baan.id}`} className="font-medium text-sm">
        {baan.label}
      </h3>

      {gesorteerd.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("promoties.geen")}</p>
      ) : (
        <ul className="space-y-2">
          {gesorteerd.map((plaats) => (
            // Never wraps. Everything but the wedstrijddag has a width of its own, so the select
            // is the one thing that gives - a `w-auto` select sizes to its widest option, which
            // here is "03-10-2026 Hoogezand-Sappemeer (Landelijke finale)" and pushed the
            // verwijderknop onto a line of its own.
            <li key={plaats.positie} className="flex items-center gap-2">
              <label
                className="shrink-0 text-sm"
                htmlFor={`positie-${baan.id}-${plaats.positie}`}
              >
                {t("promoties.plaats")}
              </label>
              <Input
                id={`positie-${baan.id}-${plaats.positie}`}
                type="number"
                min={1}
                className="w-16 shrink-0"
                value={plaats.positie}
                onChange={(e) =>
                  onChange(
                    gesorteerd.map((p) =>
                      p.positie === plaats.positie
                        ? { ...p, positie: Number(e.target.value) }
                        : p,
                    ),
                  )
                }
              />
              <span className="shrink-0 text-sm text-muted-foreground">
                {t("promoties.gaatNaar")}
              </span>
              {/* `min-w-0` so the select may shrink below its content: without it a flex item
                  refuses to go under its intrinsic width and the row overflows instead. */}
              <div className="min-w-0 flex-1">
                <Select
                  aria-label={t("promoties.bestemmingVeld", {
                    plaats: plaats.positie,
                  })}
                  value={plaats.naarWedstrijddagId}
                  onChange={(e) =>
                    onChange(
                      gesorteerd.map((p) =>
                        p.positie === plaats.positie
                          ? { ...p, naarWedstrijddagId: e.target.value }
                          : p,
                      ),
                    )
                  }
                >
                  <option value="">{t("promoties.kies")}</option>
                  {bestemmingen.map((d) => (
                    <option key={d.id} value={d.id}>
                      {label(d)}
                    </option>
                  ))}
                </Select>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label={t("promoties.verwijderPlaats", {
                  plaats: plaats.positie,
                })}
                onClick={() =>
                  onChange(
                    gesorteerd.filter((p) => p.positie !== plaats.positie),
                  )
                }
              >
                <X aria-hidden className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange([
              ...gesorteerd,
              {
                positie: Math.max(0, ...gesorteerd.map((p) => p.positie)) + 1,
                naarWedstrijddagId: "",
              },
            ])
          }
        >
          <Plus aria-hidden className="size-4" />
          {t("promoties.toevoegen")}
        </Button>
      </div>

      {zonderKlasse.length > 0 && (
        <AlertList
          tone="warning"
          title={t("promoties.zonderKlasse", {
            count: zonderKlasse.length,
            klasse: baan.klasseName,
          })}
          description={t("promoties.zonderKlasseUitleg")}
        >
          <ul className="list-disc space-y-0.5 ps-5">
            {zonderKlasse.map((d) => (
              <li key={d.id}>{label(d)}</li>
            ))}
          </ul>
        </AlertList>
      )}

      {vuil ? (
        <p className="text-sm text-muted-foreground">
          {t("promoties.nietOpgeslagen")}
        </p>
      ) : (
        baan.boekjeregels.length > 0 && (
          <div className="text-sm">
            <p className="text-muted-foreground">
              {t("promoties.inHetBoekje")}
            </p>
            <ul className="mt-1 space-y-1">
              {baan.boekjeregels.map((regel) => (
                <li key={regel} className="font-mono text-xs">
                  {regel}
                </li>
              ))}
            </ul>
          </div>
        )
      )}
    </section>
  );
}

/**
 * A deliberate exception to ADR-0028's ban on `<select>` for records: sixteen dagen at most, and
 * the whole label fits in an option.
 */
function label(dag: Wedstrijddag) {
  const plaats = dag.organiserendKorpsName ?? "";
  return `${datumKort(dag.date)} ${plaats} (${dag.niveauName})`.replace(
    "  ",
    " ",
  );
}
